import { useCallback, useEffect, useState } from 'react';
import { api } from './api.js';

interface Summary {
  id: string; name: string; aliases: string[]; category: string | null;
  klass: string | null; evidenceTier: string | null; evidenceLabel: string | null;
  halfLifeHours: number | null; routes: string[]; legalStatusZa: string | null;
}

interface Effect {
  id: string; effect: string; summary: string; onset: string | null;
  reversibility: string | null; marker: string | null; sign: string | null;
  managedWithClass: string | null; managedWithNote: string | null;
  requiresPrescription: boolean; nonDrugMitigation: string | null;
  decisionWindow: string | null; urgency: string; severity: number;
  evidenceNote: string | null; sources: string[];
}

interface Detail extends Summary {
  evidenceDetail: string | null; whatItIs: string | null; mechanism: string | null;
  wanted: string | null; unwanted: string | null; keyMarkers: string[];
  sources: string[]; effects: Effect[];
}

interface Tier { tier: string; label: string; detail: string }

const CATEGORY_LABEL: Record<string, string> = {
  androgen: 'Androgens', incretin: 'Incretins', growth: 'Growth axis',
  repair: 'Repair and cosmetic', sarm: 'SARMs', ancillary: 'Ancillaries',
  fat_loss: 'Fat loss', other: 'Other',
};

/** One colour per tier, matching the phone exactly. */
const tierClass = (t: string | null): string =>
  t === 'approved_human' ? 'chip-high' : t === 'human_trials' ? 'chip-medium' : 'chip-low';

export function Library(): JSX.Element {
  const [q, setQ] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [tier, setTier] = useState<string | null>(null);
  const [results, setResults] = useState<Summary[]>([]);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [open, setOpen] = useState<Detail | null>(null);

  useEffect(() => {
    api<{ categories: Array<{ category: string }>; tiers: Tier[] }>('/library')
      .then((r) => { setCategories(r.categories.map((c) => c.category)); setTiers(r.tiers); })
      .catch(() => undefined);
  }, []);

  const search = useCallback(async (): Promise<void> => {
    const p = new URLSearchParams({ q });
    if (category) p.set('category', category);
    if (tier) p.set('tier', tier);
    const r = await api<{ results: Summary[] }>(`/library/search?${p.toString()}`);
    setResults(r.results);
  }, [q, category, tier]);

  useEffect(() => {
    const h = setTimeout(() => { void search(); }, 200);
    return () => clearTimeout(h);
  }, [search]);

  return (
    <>
      <div className="card">
        <h2>Compounds</h2>
        <p className="muted small">
          What it is, what it does to you, what tracks that, and how well any of it is actually known.
          There are no doses here, by design.
        </p>
        <div className="row">
          <input placeholder="Search by name or nickname…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="row wrap small">
          <button type="button" className={category === null ? 'on' : ''} onClick={() => setCategory(null)}>All</button>
          {categories.map((c) => (
            <button key={c} type="button" className={category === c ? 'on' : ''}
                    onClick={() => setCategory(category === c ? null : c)}>
              {CATEGORY_LABEL[c] ?? c}
            </button>
          ))}
        </div>
        <div className="row wrap small">
          {tiers.map((t) => (
            <button key={t.tier} type="button" className={tier === t.tier ? 'on' : ''}
                    onClick={() => setTier(tier === t.tier ? null : t.tier)} title={t.detail}>
              {t.label}
            </button>
          ))}
        </div>

        <table className="tbl">
          <tbody>
            {results.map((c) => (
              <tr key={c.id}>
                <td>
                  <button type="button" onClick={() => { void api<Detail>(`/library/${encodeURIComponent(c.name)}`).then(setOpen); }}>
                    {c.name}
                  </button>
                  {c.aliases.length > 0 && <div className="muted small">{c.aliases.slice(0, 3).join(', ')}</div>}
                </td>
                <td><span className={`chip ${tierClass(c.evidenceTier)}`}>{c.evidenceLabel}</span></td>
                <td className="muted small">{CATEGORY_LABEL[c.category ?? ''] ?? c.category}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {results.length === 0 && <p className="muted small">Nothing matches. Try the nickname, or clear the filters.</p>}
      </div>

      {open && (
        <div className="card">
          <h2>{open.name}</h2>
          {open.aliases.length > 0 && <p className="muted small">Also called {open.aliases.join(', ')}</p>}

          <p><span className={`chip ${tierClass(open.evidenceTier)}`}>{open.evidenceLabel}</span></p>
          {open.evidenceDetail && <p className="muted small">{open.evidenceDetail}</p>}

          {open.whatItIs && <><h3>What it is</h3><p>{open.whatItIs}</p></>}
          {open.mechanism && <><h3>How it works</h3><p>{open.mechanism}</p></>}
          {open.wanted && <><h3>What people take it for</h3><p>{open.wanted}</p></>}
          {open.unwanted && <><h3>What else it does</h3><p>{open.unwanted}</p></>}

          <p className="muted small">
            {open.legalStatusZa && <>Legal status: {open.legalStatusZa}<br /></>}
            {open.halfLifeHours != null && <>Half-life around {open.halfLifeHours >= 48 ? `${Math.round(open.halfLifeHours / 24)} days` : `${open.halfLifeHours} hours`}<br /></>}
            {open.routes.length > 0 && <>Routes: {open.routes.join(', ')}<br /></>}
            {open.keyMarkers.length > 0 && <>Watch: {open.keyMarkers.map((m) => m.replace(/_/g, ' ')).join(', ')}</>}
          </p>

          {open.effects.length > 0 && (
            <>
              <h3>What it is known to do</h3>
              {open.effects.map((e) => (
                <div key={e.id} style={{ marginTop: 12 }}>
                  <strong>{e.effect}</strong> <span className="muted small">severity {e.severity}</span>
                  <p>{e.summary}</p>
                  {e.sign && <p className="muted small">How you would notice: {e.sign}</p>}
                  {e.marker && <p className="muted small">Tracked by: {e.marker.replace(/_/g, ' ')}</p>}
                  {e.nonDrugMitigation && (
                    <p className="small"><strong>Without a prescription:</strong> {e.nonDrugMitigation}</p>
                  )}
                  {e.managedWithNote && (
                    <p className="muted small">Clinically managed with {e.managedWithClass}. {e.managedWithNote}</p>
                  )}
                  {e.decisionWindow && <p className="muted small">{e.decisionWindow}</p>}
                  {e.evidenceNote && <p className="muted small">Evidence: {e.evidenceNote}</p>}
                </div>
              ))}
            </>
          )}

          {open.sources.length > 0 && (
            <><h3>Sources</h3><ul className="muted small">{open.sources.map((s) => <li key={s}>{s}</li>)}</ul></>
          )}
          <button type="button" onClick={() => setOpen(null)}>Close</button>
        </div>
      )}
    </>
  );
}
